import React, { useState, useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  hue: number;
  alpha: number;
  size: number;
  brightness: number;
  trail: Array<{ x: number; y: number }>;
}

interface Firework {
  x: number;
  y: number;
  vx: number;
  vy: number;
  hue: number;
  particles: Particle[];
  exploded: boolean;
}

const FireworksBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const fireworksRef = useRef<Firework[]>([]);
  const animationRef = useRef<number>(0);
  const lastFrameTime = useRef<number>(Date.now());

  const colors = {
    blue: "#0e0074",
    red: "#ea357f",
    green: "#4ffbb1"
  };

  const getColorHue = (): number => {
    const colorValues = [colors.blue, colors.red, colors.green];
    const selectedColor =
      colorValues[Math.floor(Math.random() * colorValues.length)];

    const r = parseInt(selectedColor.slice(1, 3), 16) / 255;
    const g = parseInt(selectedColor.slice(3, 5), 16) / 255;
    const b = parseInt(selectedColor.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);

    let h;

    if (max === min) {
      h = 0;
    } else if (max === r) {
      h = 60 * (0 + (g - b) / (max - min));
    } else if (max === g) {
      h = 60 * (2 + (b - r) / (max - min));
    } else {
      h = 60 * (4 + (r - g) / (max - min));
    }

    h = h < 0 ? h + 360 : h;
    return h;
  };

  const createFirework = (): Firework => {
    const x = Math.random() * dimensions.width;
    // Variable launch heights - some high (20-40%), some medium (40-60%)
    const heightVariation = Math.random() < 0.5 ? 0.3 : 0.5;
    const y = dimensions.height * (heightVariation + Math.random() * 0.2);
    const vx = (Math.random() - 0.5) * 4;
    const vy = -Math.random() * 4 - 3; // Increased upward velocity
    const hue = getColorHue();

    return {
      x,
      y,
      vx,
      vy,
      hue,
      particles: [],
      exploded: false
    };
  };

  const explodeFirework = (firework: Firework) => {
    const isFinale = Math.random() < 0.1; // 10% chance of extra-dense finale
    const particleCount = isFinale ? 10 : 6; // Minimized for optimal CPU performance
    const angleIncrement = (Math.PI * 2) / particleCount;

    for (let i = 0; i < particleCount; i++) {
      const angle = angleIncrement * i;
      const speed = Math.random() * 5 + 2; // Increased speed range
      const isCore = i % 6 === 0; // Every 6th particle is a core particle

      firework.particles.push({
        x: firework.x,
        y: firework.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        hue: firework.hue + (Math.random() - 0.5) * 30, // Slight hue variation
        alpha: 1,
        size: isCore ? Math.random() * 3 + 3 : Math.random() * 2 + 1, // Varied sizes
        brightness: isCore ? 1.5 : 1, // Core particles are brighter
        trail: []
      });
    }

    // Add secondary ring of slower particles for depth
    const secondaryCount = 2; // Minimized for optimal CPU performance
    for (let i = 0; i < secondaryCount; i++) {
      const angle = (Math.PI * 2 * i) / secondaryCount;
      const speed = Math.random() * 2 + 0.5;

      firework.particles.push({
        x: firework.x,
        y: firework.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        hue: firework.hue,
        alpha: 0.8,
        size: Math.random() * 1.5 + 0.5,
        brightness: 0.8,
        trail: []
      });
    }

    firework.exploded = true;
  };

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || dimensions.width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const clearCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "white"; // Transparent background with trail fade
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const update = () => {
      const currentTime = Date.now();
      const deltaTime = (currentTime - lastFrameTime.current) / 16.67; // Normalize to 60fps
      lastFrameTime.current = currentTime;

      clearCanvas();

      // Reduced spawn rate to 2.5%
      if (Math.random() < 0.025) {
        fireworksRef.current.push(createFirework());
      }

      // Occasional finale burst - multiple fireworks at once
      if (Math.random() < 0.0025) {
        const burstCount = 2 + Math.floor(Math.random() * 2); // 2-3 fireworks
        for (let i = 0; i < burstCount; i++) {
          setTimeout(() => {
            fireworksRef.current.push(createFirework());
          }, i * 50); // Slight delay between each
        }
      }

      // Off-screen culling: remove fireworks that are way off screen
      fireworksRef.current = fireworksRef.current.filter((firework) => {
        if (firework.exploded && firework.particles.length > 0) {
          const hasVisibleParticles = firework.particles.some(
            (p) =>
              p.x >= -50 &&
              p.x <= canvas.width + 50 &&
              p.y >= -50 &&
              p.y <= canvas.height + 50
          );
          return hasVisibleParticles;
        }
        return true;
      });

      fireworksRef.current.forEach((firework, index) => {
        if (!firework.exploded) {
          firework.x += firework.vx;
          firework.y += firework.vy;
          firework.vy += 0.05; // gravity

          ctx.beginPath();
          ctx.arc(firework.x, firework.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = `hsl(${firework.hue}, 100%, 50%)`;
          ctx.fill();

          if (firework.vy >= 0) {
            explodeFirework(firework);
          }
        } else {
          firework.particles.forEach((particle, pIndex) => {
            // Update trail
            particle.trail.push({ x: particle.x, y: particle.y });
            if (particle.trail.length > 2) {
              particle.trail.shift();
            }

            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.vy += 0.05; // gravity

            // Air resistance - slows particles down over time (ease-out effect)
            particle.vx *= 0.99;
            particle.vy *= 0.99;

            particle.alpha -= 0.008; // Slower fade

            // Twinkling effect
            particle.brightness = 0.8 + Math.random() * 0.4;

            if (particle.alpha > 0) {
              // Draw trail
              if (particle.trail.length > 1) {
                ctx.beginPath();
                ctx.moveTo(particle.trail[0].x, particle.trail[0].y);
                for (let i = 1; i < particle.trail.length; i++) {
                  ctx.lineTo(particle.trail[i].x, particle.trail[i].y);
                }
                ctx.strokeStyle = `hsla(${particle.hue}, 100%, 50%, ${particle.alpha * 0.3})`;
                ctx.lineWidth = particle.size * 0.5;
                ctx.stroke();
              }

              // Glow effect
              ctx.save();
              ctx.shadowBlur = particle.size * 8 * particle.brightness;
              ctx.shadowColor = `hsl(${particle.hue}, 100%, 60%)`;

              // Main particle
              ctx.beginPath();
              ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
              const lightness = 50 + (particle.brightness - 1) * 30;
              ctx.fillStyle = `hsla(${particle.hue}, 100%, ${lightness}%, ${particle.alpha})`;
              ctx.fill();

              ctx.restore();
            } else {
              firework.particles.splice(pIndex, 1);
            }
          });

          if (firework.particles.length === 0) {
            fireworksRef.current.splice(index, 1);
          }
        }
      });

      animationRef.current = requestAnimationFrame(update);
    };

    update();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [dimensions]);

  return (
    <div
      className="w-100 h-100 position-fixed"
      style={{ top: 0, left: 0, zIndex: -10 }}
    >
      <canvas ref={canvasRef} className="w-100 h-100" />
    </div>
  );
};

export default FireworksBackground;
